import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";

import Konn3ctHeader from "../Header & Footer/Konn3ctHeader";
import Footer from "../Header & Footer/kfooter";
import EventCarousel from "./EventCarousel";
import SearchHeader from "./SearchHeader";
import { fetchAllEvents, searchEvents } from "./EventsApi";
import { useCountry } from "../Context/CountryContext";
import { formatCurrency } from "../Utils/formatCurrency";
import { trackEvent } from "../Analytics/analytics";

const tabs = ["ALL", "TODAY", "WEEK", "MONTH", "PAST"];

const parseEventDate = (raw) => {
  const datePart = String(raw || "")
    .split("|")[0]
    .trim();
  const parsed = new Date(datePart);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const EventsPage = () => {
  const { country } = useCountry();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState({
    label: "All Locations",
    value: null,
  });

  const loadAllEvents = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const data = await fetchAllEvents(country.code);
      setEvents(data);
    } catch (error) {
      console.error("Unable to load events:", error);
      setEvents([]);
      setLoadError(error?.message || "Unable to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [country.code]);

  useEffect(() => {
    loadAllEvents();

    trackEvent("event_list_view", {
      country: country.code,
      currency: country.currency,
    });
  }, [country.code, country.currency, loadAllEvents]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term, locationValue) => {
        setLoading(true);
        setLoadError("");

        try {
          const data = await searchEvents(term, locationValue, country.code);
          setEvents(data);

          trackEvent("event_search", {
            country: country.code,
            search_term: term || undefined,
            location: locationValue || undefined,
            results: data.length,
          });
        } catch (error) {
          console.error("Event search failed:", error);
          setEvents([]);
          setLoadError(error?.message || "Unable to search events. Please try again.");
        } finally {
          setLoading(false);
        }
      }, 500),
    [country.code],
  );

  useEffect(() => {
    const hasSearch = searchTerm.trim().length > 0;
    const hasLocation = Boolean(selectedLocation?.value);

    if (!hasSearch && !hasLocation) {
      loadAllEvents();
      return undefined;
    }

    debouncedSearch(searchTerm, selectedLocation?.value || null);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, selectedLocation, debouncedSearch, loadAllEvents]);

  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return events.filter((event) => {
      const eventDate = parseEventDate(event.date);

      if (!eventDate) {
        return activeTab === "ALL";
      }

      eventDate.setHours(0, 0, 0, 0);

      if (activeTab === "TODAY") {
        return eventDate.getTime() === today.getTime();
      }

      if (activeTab === "WEEK") {
        const week = new Date(today);
        week.setDate(today.getDate() + 7);
        return eventDate >= today && eventDate <= week;
      }

      if (activeTab === "MONTH") {
        const month = new Date(today);
        month.setDate(today.getDate() + 30);
        return eventDate >= today && eventDate <= month;
      }

      if (activeTab === "PAST") {
        return eventDate < today;
      }

      return true;
    });
  }, [activeTab, events]);

  return (
    <div className="min-h-screen flex flex-col text-gray-800">
      <Konn3ctHeader />

      <main className="flex-1 px-4 sm:px-6 lg:px-16 py-10 pt-24 font-sans bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
            <div>
              <p className="text-sm font-medium text-[#1A4699]">Konn3ct Events</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">Discover upcoming events</h1>
              <p className="text-sm text-gray-500 mt-2">
                Showing the Konn3ct public event catalogue for visitors using the {country.name} experience.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mb-8">
            <SearchHeader
              countryCode={country.code}
              onSearchChange={setSearchTerm}
              onLocationChange={setSelectedLocation}
            />
          </div>

          {!loading && !loadError && events.length > 0 && <EventCarousel events={events} />}

          <div className="flex gap-6 border-b pb-2 mt-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-2 whitespace-nowrap text-sm font-medium transition ${
                  activeTab === tab ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500 hover:text-blue-600"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <h2 className="mt-6 mb-4 font-semibold text-gray-900">
            {selectedLocation?.label === "All Locations" ? "All public events" : `Events in ${selectedLocation.label}`}
          </h2>

          {loading ? (
            <div className="py-16 text-center">
              <div className="mx-auto h-9 w-9 rounded-full border-4 border-blue-100 border-t-[#1A4699] animate-spin" />
              <p className="mt-3 text-sm text-gray-500">Loading events...</p>
            </div>
          ) : loadError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-sm text-red-700">{loadError}</p>
              <button
                type="button"
                onClick={loadAllEvents}
                className="mt-4 rounded-lg bg-[#1A4699] px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              >
                Try again
              </button>
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredEvents.map((event) => {
                const displayCurrency = event.currency || country.currency;

                return (
                  <Link
                    to={`/event/${encodeURIComponent(event.ref)}`}
                    key={event.ref || event.eventId}
                    className="group border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow"
                    onClick={() =>
                      trackEvent("event_view", {
                        event_ref: event.ref,
                        event_id: event.eventId || undefined,
                        country: country.code,
                      })
                    }
                  >
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img
                        src={event.logo}
                        alt={event.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    <div className="p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#1A4699]">
                          {event.free === 1 ? "Free" : "Paid"}
                        </span>

                        <span className="text-xs font-semibold text-gray-800">
                          {event.free === 1
                            ? "FREE"
                            : formatCurrency({
                                amount: event.amount,
                                currency: displayCurrency,
                                locale: country.locale,
                              })}
                        </span>
                      </div>

                      <h3 className="font-semibold text-sm text-gray-900 line-clamp-2">{event.title}</h3>

                      <p className="text-xs text-gray-600 mt-2">By {event.organizer}</p>

                      <p className="text-xs text-gray-500 mt-1">{event.dateString}</p>

                      <p className="text-xs text-gray-400 mt-1">{event.location}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-10 text-center">
              <p className="text-gray-500">No events found.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EventsPage;
